import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormControl,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
} from '@angular/forms';
import { LcsBreakpoints, LcsScreensizeBreakpointsService } from '@lcs/shared/breakpoints';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { saveSelectedDashboardId } from 'src/app/store/user-preferences/user-preferences.actions';
import { NgChanges } from 'src/lib/ng-changes';
import { Dashboard } from '../../models/dashboard.model';

@Component({
   encapsulation: ViewEncapsulation.None,
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-dashboard-header-bar',
   templateUrl: './dashboard-header-bar.component.html',
   styleUrls: ['./dashboard-header-bar.component.scss'],
   host: {
      'class': 'dashboard-page-header',
      '[class.dashboard-page-header-editing]': 'isEditing',
   },
   standalone: false,
})
export class DashboardHeaderBarComponent implements OnChanges, OnDestroy {
   @Input() isEditing = false;

   @Input() addDashboardCancel = false;

   @Input()
   set dashboards(dashboards: Dashboard[] | null) {
      this._dashboards = dashboards;
      this.simplifiedDashboards =
         this._dashboards?.map((d) => {
            return {
               dashboardId: d.dashboardId,
               name: d.name,
            };
         }) ?? null;
   }
   get dashboards(): Dashboard[] | null {
      return this._dashboards;
   }
   private _dashboards: Dashboard[] | null;

   simplifiedDashboards: Partial<Pick<Dashboard, 'name' | 'dashboardId'>>[] | null;

   @Input() selectedDashboard: Dashboard | null;

   @Output() addTile = new EventEmitter<void>();

   @Output() addDashboard = new EventEmitter<void>();

   @Output() renameDashboard = new EventEmitter<string>();

   @Output() deleteDashboard = new EventEmitter<void>();

   @Output() editToggle = new EventEmitter<Boolean>();

   simplifiedSelectedDashboard: Partial<Pick<Dashboard, 'name' | 'dashboardId'>> | null;

   breakpoints = LcsBreakpoints;

   currentBreakpoint: LcsBreakpoints | null;

   form: UntypedFormGroup;

   get getSelectedDashboardName(): string {
      return this.form.get('name')?.value ?? '';
   }

   private destroyed$ = new Subject<void>();

   constructor(
      private store: Store,
      private screensizeBreakpointsService: LcsScreensizeBreakpointsService,
      private changeDetectorRef: ChangeDetectorRef,
      private fb: UntypedFormBuilder
   ) {
      this.screensizeBreakpointsService
         .screensizeChange()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((breakpoint: LcsBreakpoints | null) => {
            this.currentBreakpoint = breakpoint;
            this.changeDetectorRef.markForCheck();
         });

      this.initializeForm();
   }

   ngOnChanges(changes: NgChanges<DashboardHeaderBarComponent>): void {
      if (
         changes.selectedDashboard ||
         changes.isEditing ||
         (changes.addDashboardCancel !== undefined && changes.addDashboardCancel.currentValue === true)
      ) {
         this.simplifiedSelectedDashboard =
            this.simplifiedDashboards?.find((d) => d.dashboardId === this.selectedDashboard?.dashboardId) ?? null;
         this.initializeForm();
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   initializeForm() {
      this.form = this.fb.group({
         name: new UntypedFormControl(this.selectedDashboard?.name ?? '', [this.createNameValidators()]),
      });
   }

   toggleIsEditing() {
      this.initializeForm();
      this.editToggle.emit(!this.isEditing);
   }

   openAddTileDialog() {
      this.addTile.emit();
   }

   selectDashboard(dashboard: Partial<Pick<Dashboard, 'name' | 'dashboardId'>> | null) {
      this.simplifiedSelectedDashboard = dashboard;
      if (dashboard?.dashboardId) {
         this.store.dispatch(saveSelectedDashboardId({ selectedDashboardId: dashboard.dashboardId }));
      }
   }

   isSelectedDashboard(dashboard: Partial<Pick<Dashboard, 'name' | 'dashboardId'>>): boolean {
      return dashboard.dashboardId === this.simplifiedSelectedDashboard?.dashboardId;
   }

   handleCreateDashboardClick() {
      this.addDashboard.emit();
   }

   handleRenameDashboardClick() {
      this.renameDashboard.emit(this.getSelectedDashboardName);
   }

   handleDeleteDashboardClick() {
      this.deleteDashboard.emit();
   }

   createNameValidators(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const value = control.value;

         if (!value) return { required: true };
         if (value === this.selectedDashboard?.name) return null;
         if (
            !!this.dashboards?.some(
               (d) =>
                  d.name?.toLowerCase() === value.toLowerCase() && d.dashboardId !== this.selectedDashboard?.dashboardId
            )
         )
            return { duplicateName: true };

         return null;
      };
   }
}
